import { Modal, Table, type TableProps } from "antd";
import { useState } from "react";
import { useOrderHelper } from "../useOrderHelper";
import Button from "../../../../Components/Button";
import { useDeleteBranchMutation } from "../../../../hooks/BranchManagement/useDeleteBranchMutation";
import { OrderDoc } from "../orders.type";
import dayjs from "dayjs";
import { Status } from "./Status";
import { formatCurrency } from "utils/formatNumber";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OrderManagementTableHeaders } from "./OrderManagementTableHeaders";

type Props = {
  status?: string;
  search?: string;
  paymentStatus?: string;
};

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

export const OrderManagementTable = (props: Props) => {
  const [dateSearch, setDateSearch] = useState({
    startDate: "",
    endDate: "",
  });

  const navigation = useNavigate();
  const [searchParams] = useSearchParams();

  const { pagination, data, rowSelection, handleTableChange, isLoading } =
    useOrderHelper({
      status: props.status,
      paymentStatus: searchParams.get("paymentStatus") || undefined,
      deliveryMethod: searchParams.get("deliveryMethod") || undefined,
      source: searchParams.get("source") || undefined,
      startDate: dateSearch.startDate,
      endDate: dateSearch.endDate,
      search: props.search,
    });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const { mutateAsync: deleteBranchSelected, isPending: isDeleting } =
    useDeleteBranchMutation(rowSelection?.selectedRowKeys);

  const handleEdit = (branch: any) => {
    setSelectedBranch(branch);
    setIsEditModalOpen(true);
  };

  const handleDelete = (branch: any) => {
    setIsDeleteModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedBranch(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedBranch(null);
  };

  const columns: ColumnsType<OrderDoc> = [
    {
      title: "Order ID",
      dataIndex: "orderNo",
      key: "orderNo",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Delivery Method",
      dataIndex: "deliveryMethod",
      key: "deliveryMethod",
    },
    {
      title: "Amount",
      dataIndex: "totalProductsAmount",
      key: "totalProductsAmount",
      // TODO is this kobo or naira
      render: (text = 0) => formatCurrency(text / 100),
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (text) => <Status value={text} />,
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (text) => <Status value={text} />,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      // render: (text) => text,
    },
    {
      title: "Date Placed",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) =>
        text ? dayjs(text).format("MMM D, YYYY h:mm A") : "N/A",
    },
    // {
    //   title: "Actions",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (id: string, record: any) => (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       fill="none"
    //       viewBox="0 0 24 24"
    //       strokeWidth={1.5}
    //       stroke="currentColor"
    //       className="w-6 h-6 text-blue-500 cursor-pointer"
    //       onClick={() => handleEdit(record)}>
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    //       />
    //     </svg>
    //   ),
    // },
  ];

  const handleRowClick = (record: OrderDoc) =>
    navigation(`/order-management/${record.id}`);

  return (
    <div>
      <div className="pl-5 pr-10 mt-3">
        {rowSelection?.selectedRowKeys.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex gap-2 items-center bg-orange p-2 rounded-md text-white text-[12px] font-bold"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-4"
            >
              <path
                fillRule="evenodd"
                d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.035-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951Zm-6.136-1.452a51.196 51.196 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452Zm-.355 5.945a.75.75 0 1 0-1.5.058l.347 9a.75.75 0 1 0 1.499-.058l-.346-9Zm5.48.058a.75.75 0 1 0-1.498-.058l-.347 9a.75.75 0 0 0 1.5.058l.345-9Z"
                clipRule="evenodd"
              />
            </svg>
            Delete
          </button>
        )}
      </div>
      {/* {isPending && <Loader />} */}
      {/* {!isPending && data?.docs && ( */}
      <div className="rounded-lg border border-[#EEEEEE] ml-5 mr-10 manrope mt-3">
        <div className="my-2">
          <OrderManagementTableHeaders
            status={props.status}
            total={data?.meta.totalDocs}
            onDateSearchChange={(startDate, endDate) => {
              setDateSearch({
                startDate,
                endDate,
              });
            }}
          />
        </div>
        <Table
          rowKey="id"
          columns={columns}
          pagination={pagination}
          // rowSelection={rowSelection}
          onChange={handleTableChange}
          scroll={{ y: "63vh" }}
          dataSource={data?.docs}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => handleRowClick(record), // Handle row click
          })}
        />
      </div>
      {/* )} */}

      <Modal
        title={`Edit ${selectedBranch?.name}`}
        open={isEditModalOpen}
        onCancel={() => {
          closeEditModal();
        }}
        footer={null}
      >
        {/* <EditBranch
          selectedBranch={selectedBranch}
          closeEditModal={closeEditModal}
        /> */}
        Edit Branch
      </Modal>

      <Modal
        title={`Delete ${rowSelection?.selectedRowKeys.length} order${
          rowSelection?.selectedRowKeys.length > 1 ? "s" : ""
        }`}
        open={isDeleteModalOpen}
        onCancel={closeDeleteModal}
        footer={null}
      >
        <h3 className="text-[16px] maanrope mb-4">
          Confirm that you'd like to delete the selected orders
        </h3>

        <Button
          label="Delete"
          isLoading={isDeleting}
          className="text-white bg-[#f00] p-[12px] font-bold rounded-md ease-in-out duration-300"
          onClick={() => {
            deleteBranchSelected();
          }}
        />
      </Modal>
    </div>
  );
};
